import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import _ from "lodash";
import {
  catchError,
  EMPTY,
  interval,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";
import * as vis from "vis";
import { ProjectService } from "../../../core/services/project/project.service";
import { RestService } from "../../../core/services/rest.service";
import { TcgService } from "../../../core/services/tcg.service";
import { LoadingComponent } from "../../common/loading/loading.component";
import { ProgressBarComponent } from "../../common/progress-bar/progress-bar.component";

/**
 * Knowledge Graph Modal — Figma:
 * https://www.figma.com/design/nHy94WssU9Eh6xhymPpxig/Helio-Assure-Accello-?node-id=10619-7829
 *
 * Two operating modes (mirrors KnowledgeGraphComponent's isAgentic flag):
 *
 *   1. **Data mode** (data input provided):
 *      The caller sets `modalRef.componentInstance.data = <kg-response>` before
 *      the modal is shown.  ngAfterViewInit feeds that payload directly into the
 *      graph without any API calls.
 *
 *   2. **API mode** (no data input):
 *      ngOnInit calls the REST endpoints, polling until the graph is ready.
 */
@Component({
  selector: "app-knowledge-graph-modal",
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingComponent, ProgressBarComponent],
  templateUrl: "./knowledge-graph-modal.component.html",
  styleUrl: "./knowledge-graph-modal.component.scss",
})
export class KnowledgeGraphModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  // ── NgbActiveModal ──────────────────────────────────────────────────────────
  readonly activeModal = inject(NgbActiveModal);

  /** Tracks whether the close button is held (icon-swap effect). */
  isCloseActive = false;

  // ── Data input (optional) ───────────────────────────────────────────────────
  /**
   * Pre-fetched KG response object.  When provided the modal loads the graph
   * directly without making any API calls.  Set this on the modal ref:
   *
   *   const ref = this.modalService.open(KnowledgeGraphModalComponent, {...});
   *   ref.componentInstance.data = kgResponsePayload;
   */
  @Input() data: any;

  /** Required for API mode — project UUID. */
  @Input() projectId = "";
  /** Required for API mode — extraction config UUID. */
  @Input() configId = "";
  /** Story keys to request for story-driven KG (API mode). */
  @Input() storyKeys: string[] = [];
  /** Whether all stories are selected (API mode). */
  @Input() selectedAll = true;

  // ── Graph data ──────────────────────────────────────────────────────────────
  dataSet: any = { nodes: [], edges: [] };
  clonedData: any = {};
  /** 'appContext' = Unified (first, default); 'storyDriven' = Story-Driven */
  activeType: "appContext" | "storyDriven" = "appContext";

  // ── Story multi-select state ─────────────────────────────────────────────────
  /** All unique user_story_id values present in the data. */
  allStoryIds: string[] = [];
  /** Currently checked stories (default = all). */
  selectedStoryIds: string[] = [];
  /** Controls the dropdown panel visibility. */
  isStoryDropdownOpen = false;

  // ── Weight chip filter (story-driven only) ────────────────────────────────────
  filterData: any = { weight: "" };
  filterOptions: any = { weights: [] };

  // ── Loading / progress ──────────────────────────────────────────────────────
  isLoading = false;
  isDownloading = false;
  displayProgress = false;
  isProgressError = false;
  progressText = "";
  percentage = 0;

  // ── Service state (API mode only) ───────────────────────────────────────────
  selectedStories: any = [];
  selectedRows: any;
  deselectedRows: any;
  isSelectedAll = false;
  deSelectedStoryKeys: any = [];

  // ── vis-network ─────────────────────────────────────────────────────────────
  @ViewChild("networkContainer") networkContainer!: ElementRef;
  networkInstance: vis.Network | undefined;

  readonly visOptions = {
    navigationButtons: true,
    interaction: {
      hover: true,
      edges: { arrows: "to", shadow: true },
    },
  };

  // ── Colour palette ──────────────────────────────────────────────────────────
  colors: any[] = [
    { color: "darkcyan" }, { color: "green" }, { color: "blue" },
    { color: "burlywood" }, { color: "blueviolet" }, { color: "purple" },
    { color: "crimson" }, { color: "teal" }, { color: "tomato" },
    { color: "gold" }, { color: "orange" }, { color: "darkorange" },
    { color: "deeppink" }, { color: "mediumseagreen" }, { color: "seagreen" },
    { color: "slateblue" }, { color: "steelblue" }, { color: "lightseagreen" },
    { color: "coral" }, { color: "firebrick" }, { color: "darkslategray" },
    { color: "darkolivegreen" }, { color: "indianred" }, { color: "khaki" },
    { color: "lightcoral" }, { color: "mediumorchid" },
    { color: "mediumturquoise" }, { color: "navy" }, { color: "olive" },
    { color: "orchid" }, { color: "peru" }, { color: "rosybrown" },
    { color: "salmon" }, { color: "sienna" }, { color: "springgreen" },
    { color: "turquoise" }, { color: "violet" },
  ];
  colorsByType: any = {};

  private readonly stopPolling$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private viewReady = false;
  private pendingResponse: any = null;

  constructor(
    private readonly el: ElementRef,
    private readonly projectService: ProjectService,
    private readonly restService: RestService,
    private readonly tcgService: TcgService,
  ) {}

  // ── Click-outside: close story dropdown ──────────────────────────────────────
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event): void {
    if (!this.isStoryDropdownOpen) return;
    const multiselect: HTMLElement | null =
      this.el.nativeElement.querySelector(".kg-multiselect");
    const target = event.target as Node | null;
    if (multiselect && target && !multiselect.contains(target)) {
      this.isStoryDropdownOpen = false;
    }
  }

  // ── Computed helpers ─────────────────────────────────────────────────────────
  get isAllStoriesSelected(): boolean {
    return this.allStoryIds.length > 0 &&
      this.selectedStoryIds.length === this.allStoryIds.length;
  }

  get isSomeStoriesSelected(): boolean {
    return this.selectedStoryIds.length > 0 && !this.isAllStoriesSelected;
  }

  get storyDropdownLabel(): string {
    if (this.selectedStoryIds.length === 0) return "None";
    if (this.isAllStoriesSelected) return "All";
    if (this.selectedStoryIds.length <= 2) return this.selectedStoryIds.join(", ");
    return `${this.selectedStoryIds.length} selected`;
  }

  isStorySelected(id: string): boolean {
    return this.selectedStoryIds.includes(id);
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // If data was injected by the caller, skip all API calls — the graph will
    // be populated in ngAfterViewInit once the view is ready.
    if (this.data) return;

    // ── API mode ────────────────────────────────────────────────────────────
    this.isLoading = true;

    // Prefer explicit inputs; fall back to TCG / project services (legacy flow)
    if (!this.configId) {
      this.configId = this.tcgService.getConfigId();
    }
    if (this.storyKeys?.length) {
      this.selectedStories = this.storyKeys;
      this.isSelectedAll = this.selectedAll;
      this.deSelectedStoryKeys = [];
      this.loadKgNodes();
      return;
    }

    this.selectedStories = this.tcgService.getStoryKeys();

    this.tcgService.getSelectedRows().subscribe((res: any) => {
      this.selectedRows = res;
    });
    this.tcgService.getDeSelectedRows().subscribe((res: any) => {
      this.deselectedRows = res;
    });
    this.tcgService.getSelectedStatus().subscribe((res: any) => {
      this.isSelectedAll = res;
    });

    if (this.projectId) {
      this.deSelectedStoryKeys = (this.deselectedRows ?? []).map(
        (item: any) => item.storyId
      );
      this.loadKgNodes();
      return;
    }

    this.projectService.getProjectId().subscribe((res: any) => {
      this.projectId = res;
      this.deSelectedStoryKeys = (this.deselectedRows ?? []).map(
        (item: any) => item.storyId
      );
      this.loadKgNodes();
    });
  }

  private loadKgNodes(): void {
    const payload = {
      selectedStoryKeys: this.selectedStories,
      selectedAll: this.isSelectedAll,
      excludedStoryKeys: this.deSelectedStoryKeys ?? [],
    };

    this.restService
      .getKgNodes(this.projectId, this.configId, payload)
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          if (res.data?.status === "COMPLETED") {
            this.onNodesResponseReceived(res);
            this.getUnifiedNodes();
          } else {
            this.progressText = res?.data?.message ?? "Initializing";
            this.getNodesAsync();
          }
        },
        error: (err: any) => {
          this.isLoading = false;
          this.isProgressError = true;
          this.displayProgress = true;
          this.progressText =
            err?.error?.message || err?.message || "Failed to load knowledge graph";
        },
      });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    // ── Data mode: use the injected payload directly ─────────────────────────
    if (this.data) {
      this.onNodesResponseReceived(this.data);
    } else if (this.pendingResponse) {
      // API response arrived before the view was ready
      this.onNodesResponseReceived(this.pendingResponse);
      this.pendingResponse = null;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data fetching (API mode) ─────────────────────────────────────────────────

  getNodesAsync(): void {
    this.displayProgress = true;
    this.percentage = 5;
    const payload = {
      selectedStoryKeys: this.selectedStories,
      selectedAll: this.isSelectedAll,
      excludedStoryKeys: this.deSelectedStoryKeys,
    };

    interval(5000)
      .pipe(
        switchMap(() =>
          this.restService.getKgNodes(this.projectId, this.configId, payload)
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError(() => {
          this.progressText = "Error occurred while generating graph";
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        if (res.data?.status === "FAILED") {
          this.progressText = res?.data?.error ?? res?.data?.message;
          this.isProgressError = true;
          this.stopPolling$.next();
        } else if (res.data?.status !== "COMPLETED") {
          this.progressText = res?.data?.message ?? "Processing graph nodes";
          if (this.percentage + 15 < 90) this.percentage += 15;
        } else {
          this.stopPolling$.next();
          this.progressText = "Process completed";
          this.onNodesResponseReceived(res);
          this.getUnifiedNodes(!res?.data?.nodes?.length);
          setTimeout(() => (this.displayProgress = false), 1000);
          this.percentage = 100;
        }
      });
  }

  getUnifiedNodes(setActive = false): void {
    this.isLoading = true;
    this.restService
      .getUnifiedKgNodes(this.projectId, this.configId)
      .subscribe({
        next: (res: any) => {
          this.clonedData.appContext = _.cloneDeep(res.data);
          if (setActive) {
            this.dataSet = res.data;
            this.activeType = "appContext";
            this.populateGraph();
          }
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
  }

  // ── Shared: process nodes response ───────────────────────────────────────────

  onNodesResponseReceived(res: any): void {
    // Accept both { nodes, edges } and wrapped { data: { nodes, edges } }
    const payload = res?.data ?? res;

    // Both types share the same raw dataset — story-driven filters on demand.
    this.clonedData.appContext = _.cloneDeep(payload);
    this.clonedData.storyDriven = _.cloneDeep(payload);

    // Default: Unified view (all data, no filtering)
    this.activeType = "appContext";
    this.dataSet = this.clonedData.appContext;

    // Extract all story IDs from ALL nodes and default-select them all
    this._extractAllStoryIds();

    if (!this.viewReady || !this.networkContainer) {
      this.pendingResponse = res;
      return;
    }
    this.populateGraph();
  }

  /** Collect every unique user_story_id from the raw dataset. */
  private _extractAllStoryIds(): void {
    const ids = new Set<string>();
    (this.clonedData.appContext?.nodes ?? []).forEach((node: any) => {
      const rawId = node.properties?.user_story_id;
      if (!rawId) return;
      (Array.isArray(rawId) ? rawId : [rawId]).forEach((id: string) => id && ids.add(id));
    });
    this.allStoryIds = Array.from(ids).sort();
    this.selectedStoryIds = [...this.allStoryIds]; // all checked by default
  }

  /**
   * Builds `dataSet` for Story-Driven mode: only nodes whose user_story_id
   * intersects with `selectedStoryIds`.  System nodes (no user_story_id) are
   * excluded in this view.
   */
  private _buildStoryDrivenDataSet(): void {
    const allNodes: any[] = this.clonedData.storyDriven?.nodes ?? [];

    const filteredNodes = allNodes.filter((n: any) => {
      const rawId = n.properties?.user_story_id;
      if (!rawId) return false;
      const ids: string[] = Array.isArray(rawId) ? rawId : [rawId];
      return ids.some((id) => this.selectedStoryIds.includes(id));
    });

    const nodeIds = new Set(filteredNodes.map((n: any) => n.id));
    const filteredEdges = (this.clonedData.storyDriven?.edges ?? []).filter(
      (e: any) => nodeIds.has(e.source) && nodeIds.has(e.target)
    );

    this.dataSet = { nodes: filteredNodes, edges: filteredEdges };
  }

  private _populateWeightOptions(): void {
    // Only include weight values that exist in the currently filtered dataSet.
    const seen = new Map<string, { low: any; high: any }>();
    (this.dataSet.nodes ?? []).forEach((node: any) => {
      const low = node.properties?.weight?.low;
      const high = node.properties?.weight?.high;
      if (low !== undefined && high !== undefined) {
        const key = `${low},${high}`;
        if (!seen.has(key)) seen.set(key, { low, high });
      }
    });
    // Sort descending by low value (5–0, 4–0, 3–0, 2–0 …)
    this.filterOptions.weights = Array.from(seen.values())
      .sort((a, b) => b.low - a.low)
      .map(({ low, high }) => ({ value: `${low},${high}`, low, high }));
  }

  private _resetColors(): void {
    this.colors.forEach((c: any) => delete c.used);
    this.colorsByType = {};
  }

  // ── Graph rendering ──────────────────────────────────────────────────────────

  populateGraph(): void {
    this.networkInstance?.redraw();

    const nodes = new vis.DataSet<any>(
      this.dataSet.nodes.map((ele: any) => ({
        ...ele,
        shape: "dot",
        title: ele?.properties?.user_story_id
          ? `[${ele.properties.user_story_id}] ${ele.properties.description}`
          : `${ele?.properties?.description}`,
        size: 8,
        color: this.colorsByType[ele.type] ?? this.getRandomColor(ele.type),
        font: { size: 10, bold: true },
      }))
    );

    const edges = new vis.DataSet<any>(
      this.dataSet.edges.map((ele: any) => ({
        ...ele,
        from: ele.source,
        to: ele.target,
        label: ele.relationship,
        title: ele?.properties?.name,
        font: { size: 8, color: "#808080" },
        arrows: { to: { enabled: true, scaleFactor: 0.5 } },
      }))
    );

    this.networkInstance = new vis.Network(
      this.networkContainer.nativeElement,
      { nodes, edges },
      this.visOptions
    );
  }

  // ── Graph type switching ──────────────────────────────────────────────────────

  onTypeSelect(type: "appContext" | "storyDriven"): void {
    if (this.activeType === type) return;
    this.activeType = type;
    this.filterData.weight = "";
    this.filterOptions.weights = [];
    this._resetColors();

    if (type === "appContext") {
      // Unified: show ALL nodes and edges
      this.dataSet = _.cloneDeep(this.clonedData.appContext);
    } else {
      // Story-Driven: filter by selectedStoryIds then populate weight chips
      this._buildStoryDrivenDataSet();
      this._populateWeightOptions();
    }
    this.populateGraph();
  }

  // ── Story multi-select ────────────────────────────────────────────────────────

  toggleStoryDropdown(event: Event): void {
    event.stopPropagation();
    this.isStoryDropdownOpen = !this.isStoryDropdownOpen;
  }

  toggleAllStories(event: Event): void {
    event.stopPropagation();
    this.selectedStoryIds = this.isAllStoriesSelected ? [] : [...this.allStoryIds];
    this._onStorySelectionChanged();
  }

  toggleStoryId(event: Event, id: string): void {
    event.stopPropagation();
    this.selectedStoryIds = this.isStorySelected(id)
      ? this.selectedStoryIds.filter((s) => s !== id)
      : [...this.selectedStoryIds, id];
    this._onStorySelectionChanged();
  }

  private _onStorySelectionChanged(): void {
    if (this.activeType !== "storyDriven") return;
    this.filterData.weight = "";
    this.filterOptions.weights = [];
    this._resetColors();
    this._buildStoryDrivenDataSet();
    this._populateWeightOptions();
    this.populateGraph();
  }

  // ── Weight chip filter ────────────────────────────────────────────────────────

  onWeightChipSelect(value: string): void {
    this.filterData.weight = value;
    this._resetColors();

    // Rebuild story-filtered base, then optionally apply weight on top
    this._buildStoryDrivenDataSet();

    if (value) {
      const [lowStr, highStr] = value.split(",");
      const wLow = JSON.parse(lowStr);
      const wHigh = JSON.parse(highStr);

      const filteredNodes = this.dataSet.nodes.filter(
        (n: any) =>
          n?.properties?.weight?.low === wLow &&
          n?.properties?.weight?.high === wHigh
      );
      const nodeIds = new Set(filteredNodes.map((n: any) => n.id));
      const filteredEdges = this.dataSet.edges.filter(
        (e: any) => nodeIds.has(e.source) || nodeIds.has(e.target)
      );
      this.dataSet = { nodes: filteredNodes, edges: filteredEdges };
    }

    this.populateGraph();
  }

  getRandomColor(type: string): string {
    const available = this.colors.filter((c: any) => !c.used);
    if (!available.length) return "";
    const obj = available[Math.floor(Math.random() * available.length)];
    const idx = this.colors.findIndex((c: any) => c.color === obj.color);
    this.colors[idx].used = true;
    this.colorsByType[type] = obj.color;
    return obj.color;
  }

  // ── Download ─────────────────────────────────────────────────────────────────
  downloadGraphAsHTML() {
    console.log(this.filterData.userStory);
    this.isDownloading = true;
    const download$ =
      this.activeType === "storyDriven"
        ? this.restService.downloadKnowledgeGraph(
            this.projectId,
            this.configId,
            {
              selectedStoryKeys: [...this.selectedStories],
              selectedAll: this.isSelectedAll,
              excludedStoryKeys: this.deSelectedStoryKeys,
            }
          )
        : this.restService.downloadUnifiedKnowledgeGraph(
            this.projectId,
            this.configId
          );

    download$.subscribe({
      next: (res: any) => {
        this.isDownloading = false;
        if (!res?.data?.html) {
          console.error("No HTML content found in response");
          return;
        }

        try {
          const blob = new Blob([res.data.html], { type: "text/html" });
          const url = URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.download = "knowledge-graph.html";
          document.body.appendChild(link); // Firefox fix
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(url); // cleanup
        } catch (err) {
          console.error("Error creating or downloading the file:", err);
        }
      },
      error: (err) => {
        this.isDownloading = false;
        console.error("Download failed:", err);
      },
    });
  }

}
